import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Art } from '../components/Art';
import { Chip, InlineNote, Overline, TopBar, useNote } from '../components/ui';
import { Icon, type IconName } from '../components/Icon';
import { Screen } from '../components/Screen';
import { Stage } from '../components/Stage';
import { header_search } from '../art/art';
import { useApp } from '../lib/app';
import { km, minutes, relativeDay, rupees } from '../lib/format';
import { haversine } from '../lib/geo';
import { useBack } from '../lib/nav';
import { shortName } from '../lib/places';
import { addressOf, landmarkRows, namePoint, placesUp, searchPlaces, type PlaceRow } from '../lib/placesApi';
import { fareFor, getQuote } from '../lib/quotes';
import { tripParams } from '../lib/tripUrl';
import { useRecentPlaces } from '../lib/useRecents';
import { EmptyArt } from '../art/Empty';
import type { Place, Quote } from '../lib/types';
import { words } from '../lib/vehicles';
import { useT } from '../lib/i18n';
import { toPlace as savedToPlace } from '../lib/savedPlaces';
import { useSavePlace } from './placeSave';

/** One line of the list: a search result, a recent place, or "current location". */
type Row = { id: string; name: string; sub: string; place: Place | null; highlight: number; icon: IconName; here?: boolean; priced: boolean };

/**
 * Where from, where to. Both are editable (tap the pickup line to change it, Uber-style); choosing a drop goes
 * straight to Choose ride, choosing a pickup moves on to the drop (or back to Choose ride when there is one).
 * Before typing: "Current location" (pickup) and recent drop-offs. On a keyboard: ↑ / ↓ pick a line, Enter takes
 * it, Esc goes back.
 */
export function Search() {
  const { field = 'drop' } = useSearch({ from: '/search' });
  const { api, draft, setDraft, rider, locate } = useApp();
  const t = useT();
  const nav = useNavigate();
  const back = useBack('/home');
  const { save, sheet } = useSavePlace();
  const saving = field === 'home' || field === 'work' || field === 'place';
  const [active, setActive] = useState<'pickup' | 'drop'>(field === 'pickup' ? 'pickup' : 'drop');
  const [q, setQ] = useState('');
  const [quotes, setQuotes] = useState<Record<string, Quote | null>>({});
  const pickupInput = useRef<HTMLInputElement>(null);
  const dropInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    (active === 'pickup' ? pickupInput : dropInput).current?.focus();
  }, [active]);

  const [locNote, sayLoc] = useNote();
  const [locating, setLocating] = useState(false);
  const recents = useRecentPlaces(5);
  // typing: Ola's suggestions 250 ms after the last key; stale answers are dropped; the list keeps the last answer
  // while the next one is on its way. The local landmark list only answers when Ola is down.
  const [found, setFound] = useState<PlaceRow[]>([]);
  const [searching, setSearching] = useState(false);
  const asked = useRef(0);
  const query = q.trim();
  useEffect(() => {
    const n = ++asked.current;
    if (!query) {
      setSearching(false);
      setFound([]);
      return;
    }
    if (query.length < 2 || !placesUp()) {
      setSearching(false);
      setFound(landmarkRows(query));
      return;
    }
    setSearching(true);
    const tm = window.setTimeout(() => {
      void searchPlaces(api, query, draft.pickup).then((r) => {
        if (n !== asked.current) return;
        setFound(r);
        setSearching(false);
      });
    }, 250);
    return () => window.clearTimeout(tm);
  }, [api, query, draft.pickup]);

  const pricing = !saving && active === 'drop';
  const rows: Row[] = useMemo(() => {
    if (query) return found.map((m) => ({ id: m.id, name: m.name, sub: m.sub, place: m.place, highlight: m.highlight, icon: 'pin' as IconName, priced: pricing }));
    const out: Row[] = [];
    if (active === 'pickup' && !saving) out.push({ id: 'here', name: t('loc.current'), sub: '', place: null, highlight: 0, icon: 'locate', here: true, priced: false });
    for (const r of recents ?? []) out.push({ id: `recent:${r.place.lat},${r.place.lng}`, name: shortName(r.place.address), sub: [r.place.address?.split(',').slice(1).join(',').trim(), relativeDay(r.at)].filter(Boolean).join(' · '), place: r.place, highlight: 0, icon: 'clock', priced: pricing });
    return out;
  }, [query, found, active, saving, recents, pricing, t]);

  const [hi, setHi] = useState(-1);
  useEffect(() => setHi(-1), [query, active]);

  // fares for the first few lines (the same signed quotes Choose ride will use, so the price doesn't change)
  useEffect(() => {
    if (!pricing) return;
    let live = true;
    const tm = window.setTimeout(() => {
      for (const m of rows.slice(0, 3)) {
        if (!m.place || haversine(draft.pickup, m.place) < 300) continue;
        getQuote(api, draft.pickup, m.place).then(
          (qq) => live && setQuotes((s) => ({ ...s, [m.id]: qq })),
          () => live && setQuotes((s) => ({ ...s, [m.id]: null })),
        );
      }
    }, 250);
    return () => {
      live = false;
      window.clearTimeout(tm);
    };
  }, [api, rows, draft.pickup, pricing]);
  const prefetch = (p: Place | null) => p && pricing && haversine(draft.pickup, p) >= 300 && void getQuote(api, draft.pickup, p).catch(() => undefined);

  const home = rider?.places.find((p) => p.label === 'HOME');
  const work = rider?.places.find((p) => p.label === 'WORK');
  const others = (rider?.places ?? []).filter((p) => p.label === 'OTHER');

  async function takeHere() {
    setLocating(true);
    const at = await locate();
    if (!at) {
      setLocating(false);
      return sayLoc(t('pickup.locOff'));
    }
    const name = await namePoint(api, at);
    setLocating(false);
    if (name.outside) return sayLoc(t('err.OUTSIDE_SERVICE_AREA'), 'error');
    choose({ ...at, address: addressOf(name) }, null, true);
  }

  function choose(place: Place, quote: Quote | null = null, here = false) {
    if (saving) {
      save(field, place, () => nav({ to: field === 'place' ? '/account/places' : '/home' }));
      return;
    }
    if (active === 'pickup') {
      setDraft({ pickup: place, pickupIsHere: here, quote: null });
      setQ('');
      // a drop already chosen: straight back to the ride options for the new pickup; else ask where to
      if (draft.drop) nav({ to: '/choose', search: tripParams(place, draft.drop), replace: field === 'pickup' });
      else setActive('drop');
      return;
    }
    setDraft({ drop: place, quote });
    nav({ to: '/choose', search: tripParams(draft.pickup, place) });
  }
  const take = (r: Row) => (r.here ? void takeHere() : r.place && choose(r.place, quotes[r.id] ?? null));

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!rows.length) return;
      const n = e.key === 'ArrowDown' ? (hi + 1) % rows.length : hi <= 0 ? rows.length - 1 : hi - 1;
      setHi(n);
      document.getElementById(`zr-row-${n}`)?.scrollIntoView({ block: 'nearest' });
      prefetch(rows[n].place);
    } else if (e.key === 'Enter') {
      const r = rows[hi >= 0 ? hi : 0];
      if (r && (hi >= 0 || query)) {
        e.preventDefault();
        take(r);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (q) setQ('');
      else back();
    }
  };

  const title =
    field === 'home' ? t('search.setHome', { name: home?.name ?? t('home.home') }) : field === 'work' ? t('search.setHome', { name: work?.name ?? t('home.work') }) : field === 'place' ? t('search.setPlace') : active === 'pickup' ? t('search.pickup') : t('search.trip');
  const typeLabel = words(t, draft.vehicleType).label.toUpperCase();
  const pickupLabel = draft.pickupIsHere ? t('loc.current') : (draft.pickup.address ?? '');
  const listId = 'zr-search-list';
  const inputProps = (which: 'pickup' | 'drop') => ({
    type: 'search' as const,
    enterKeyHint: 'search' as const,
    autoComplete: 'off',
    'aria-controls': listId,
    'aria-activedescendant': active === which && hi >= 0 ? `zr-row-${hi}` : undefined,
    onKeyDown: onKey,
  });

  return (
    <Screen name="search" label={title}>
      <Stage top={0} height={104}>
        <Art piece={header_search} />
      </Stage>
      <TopBar onBack={back} title={title} style={{ position: 'absolute', left: 0, right: 0, top: 'calc(var(--sat) + 10px)' }} />

      <div className="zr-scroll" style={{ top: 'calc(var(--sat) + 68px)', padding: '0 20px calc(var(--sab) + 16px)' }}>
        <div style={{ position: 'relative', borderRadius: 22, background: 'var(--s1)', padding: '0 8px 0 0' }}>
          {!saving && (
            <>
              <span aria-hidden="true" style={{ position: 'absolute', left: 23, top: 33, width: 2, height: 42, backgroundImage: 'linear-gradient(var(--ink3) 50%, transparent 50%)', backgroundSize: '2px 6px', opacity: 0.6 }} />
              <div className="zr-field-row" data-active={active === 'pickup'} style={{ height: 56, display: 'flex', alignItems: 'center', gap: 14, paddingLeft: 18 }}>
                <span aria-hidden="true" style={{ flex: 'none', width: 12, height: 12, borderRadius: 6, boxSizing: 'border-box', border: '3px solid var(--acText)' }} />
                <input
                  ref={pickupInput}
                  {...inputProps('pickup')}
                  value={active === 'pickup' ? q : pickupLabel}
                  readOnly={active !== 'pickup'}
                  onFocus={() => active !== 'pickup' && (setActive('pickup'), setQ(''))}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={pickupLabel || t('search.pickup')}
                  aria-label={t('search.pickup')}
                  style={{ ...inputStyle, fontWeight: active === 'pickup' ? 700 : 500, color: active === 'pickup' ? 'var(--ink)' : 'var(--ink2)' }}
                />
              </div>
              <div aria-hidden="true" style={{ marginLeft: 44, height: 1, background: 'var(--line)' }} />
            </>
          )}
          <div className="zr-field-row" data-active={active === 'drop'} style={{ height: 56, display: 'flex', alignItems: 'center', gap: 14, paddingLeft: 18 }}>
            <span aria-hidden="true" style={{ flex: 'none', width: 12, height: 12, borderRadius: 3, background: 'var(--ink)' }} />
            <input
              ref={dropInput}
              {...inputProps('drop')}
              value={active === 'drop' ? q : (draft.drop?.address ?? '')}
              readOnly={active !== 'drop'}
              onFocus={() => active !== 'drop' && (setActive('drop'), setQ(''))}
              onChange={(e) => setQ(e.target.value)}
              placeholder={saving ? t('search.searchPlace') : t('search.whereTo')}
              aria-label={t('search.whereTo')}
              style={{ ...inputStyle, fontWeight: active === 'drop' ? 700 : 500, color: active === 'drop' ? 'var(--ink)' : 'var(--ink2)' }}
            />
            {q && (
              <button type="button" aria-label={t('search.clear')} onClick={() => (setQ(''), (active === 'pickup' ? pickupInput : dropInput).current?.focus())} style={{ flex: 'none', width: 44, height: 44, border: 0, padding: 0, background: 'transparent', color: 'var(--ink2)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <span style={{ width: 24, height: 24, borderRadius: 12, background: 'var(--s3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name="close" size={12} sw={3} />
                </span>
              </button>
            )}
          </div>
        </div>

        <div style={{ marginTop: 12, display: 'flex', gap: 8, overflowX: 'auto', scrollbarWidth: 'none', margin: '12px -20px 0', padding: '0 20px' }}>
          <Chip icon={active === 'pickup' ? 'pin' : 'locate'} onClick={() => nav({ to: '/pickup', search: { pick: saving ? field : active } })}>
            {t('search.onMap')}
          </Chip>
          {!saving && (
            <Chip icon="home" onClick={() => (home ? choose(savedToPlace(home)) : nav({ to: '/search', search: { field: 'home' } }))}>
              {home?.name ?? t('home.home')}
            </Chip>
          )}
          {!saving && (
            <Chip icon="briefcase" onClick={() => (work ? choose(savedToPlace(work)) : nav({ to: '/search', search: { field: 'work' } }))}>
              {work?.name ?? t('home.work')}
            </Chip>
          )}
          {!saving &&
            others.map((p) => (
              <Chip key={`${p.name}-${p.lat}`} icon="pin" onClick={() => choose(savedToPlace(p))}>
                {p.name}
              </Chip>
            ))}
        </div>

        <InlineNote note={locNote} />
        <div style={{ marginTop: 22, display: 'flex', justifyContent: 'space-between' }}>
          <Overline style={{ fontSize: 11.5, letterSpacing: '0.1em', display: 'flex', alignItems: 'center', gap: 8 }}>
            {query ? t('search.places') : t('search.recent')}
            {searching && <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: 3, background: 'var(--ac)', animation: 'zr-breathe 1s ease-in-out infinite' }} />}
          </Overline>
          {pricing && rows.some((r) => r.priced) && <Overline style={{ fontSize: 11.5, letterSpacing: '0.1em' }}>{t('search.fixedBy', { type: typeLabel })}</Overline>}
        </div>
        <div id={listId} key={query ? 'q' : 'near'} className="zr-stagger" style={{ marginTop: 4, display: 'flex', flexDirection: 'column' }}>
          {!query && recents == null && [0, 1, 2].map((i) => <span key={i} aria-hidden="true" className="zr-skeleton" style={{ height: 50, margin: '8px 0', borderRadius: 14 }} />)}
          {rows.length === 0 && !searching && (query || recents != null) && (
            <div style={{ margin: '18px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 12 }}>
              <EmptyArt kind="search" width={130} />
              <p style={{ margin: 0, fontSize: 14, lineHeight: '20px', fontWeight: 500, color: 'var(--ink2)' }}>{query ? t('search.none') : t('search.cantFind')}</p>
            </div>
          )}
          {rows.map((m, i) => {
            const qq = quotes[m.id];
            const fare = m.priced ? fareFor(qq ?? null, draft.vehicleType) : null;
            const d = m.place ? (qq ? km(qq.distanceMeters) : km(haversine(draft.pickup, m.place) * 1.35)) : '';
            const sub = m.here ? (locating ? t('loc.finding') : '') : `${m.sub ? `${m.sub} · ` : ''}${d}${qq && i === 0 ? ` · ${t('search.about', { n: minutes(qq.durationSeconds) })}` : ''}`;
            return (
              <button
                key={m.id}
                id={`zr-row-${i}`}
                type="button"
                onClick={() => take(m)}
                onPointerEnter={() => prefetch(m.place)}
                onPointerDown={() => prefetch(m.place)}
                data-active={i === hi}
                className="zr-press-soft zr-result"
                style={{ minHeight: 66, border: 0, background: undefined, display: 'flex', alignItems: 'center', gap: 14, color: 'var(--ink)', textAlign: 'left', boxShadow: i < rows.length - 1 ? 'inset 0 -1px 0 var(--line)' : undefined, cursor: 'pointer', fontFamily: 'inherit' }}
              >
                <span aria-hidden="true" style={{ flex: 'none', width: 40, height: 40, borderRadius: 13, background: m.here ? 'var(--acSoft)' : 'var(--s1)', color: m.here ? 'var(--acText)' : 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name={m.icon} size={20} sw={1.9} />
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 16, lineHeight: '21px', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {m.highlight ? (
                      <>
                        <span style={{ color: 'var(--acText)' }}>{m.name.slice(0, m.highlight)}</span>
                        {m.name.slice(m.highlight)}
                      </>
                    ) : (
                      m.name
                    )}
                  </span>
                  {sub && <span className="zr-ellipsis" style={{ display: 'block', fontSize: 13, lineHeight: '18px', fontWeight: 500, color: 'var(--ink2)' }}>{sub}</span>}
                </span>
                {m.priced && (fare != null ? <span key={fare} className="zr-num zr-tick" style={{ fontSize: 17, lineHeight: '21px', fontWeight: 700 }}>{rupees(fare)}</span> : qq === undefined && i < 3 && m.place && haversine(draft.pickup, m.place) >= 300 ? <span aria-hidden="true" className="zr-skeleton" style={{ width: 38, height: 14, borderRadius: 7 }} /> : null)}
              </button>
            );
          })}
        </div>
        {query && (
          <p style={{ margin: '18px 0 0', fontSize: 12, lineHeight: '17px', fontWeight: 500, color: 'var(--ink3)' }}>
            {t('search.cantFind')}
          </p>
        )}
      </div>
      {sheet}
    </Screen>
  );
}

const inputStyle = { flex: 1, minWidth: 0, height: 56, border: 0, outline: 0, padding: 0, background: 'transparent', color: 'var(--ink)', fontSize: 16, fontWeight: 700, textOverflow: 'ellipsis' } as const;
